/**
 * SmartLink targeting hub — tabbed page component.
 *
 * One route, eight tabs. Each tab hits its own API:
 *   Geo | OS | Device | Carrier | Cookieless | Mode | Split | Gate
 *
 * Shared state lives in the URL (`?smartlinkId=...&tab=...`) so operators can
 * deep-link into any tab for any SmartLink. The active smartlink_id propagates
 * to every sub-form via the `smartlinkId` prop — no React context needed for a
 * form surface this small.
 *
 * Styling follows the Trackerity dashboard brief:
 *   - light theme only, Inter font (root-owned CSS)
 *   - emerald -> sky brand gradient only on the page heading
 *   - 6-8px rounded corners, zinc borders, subtle shadows
 *   - no emojis, no competitor naming
 */

import { useMemo, useState, useEffect, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	TARGETING_TABS,
	findTab,
	type TargetingTabSlug,
	type GeoConfig,
	type OsConfig,
	type DeviceConfig,
	type CarrierConfig,
	type CookielessConfig,
	type ModeConfig,
	type SplitConfig,
	type GateConfig,
	type DeviceClass,
	type SplitRule,
	parseSmartlinkId,
	parseCsvCodes,
	parseDeviceList,
	parseSplitRules,
	defaultConfigFor,
	fetchTargetingList,
	fetchTargetingOne,
	saveTargeting,
	smartlinksTargetingQueryKeys,
	type TargetingRow,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — co-located to keep JSX scannable. Light theme only.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:border-emerald-700 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page props — route component injects `initialSmartlinkId` from URL search.
// `initialTab` defaults to the first tab (geo).
// ---------------------------------------------------------------------------

export interface SmartlinkTargetingPageProps {
	initialSmartlinkId?: string;
	initialTab?: TargetingTabSlug;
	onTabChange?: (tab: TargetingTabSlug) => void;
}

export function SmartlinkTargetingPage(props: SmartlinkTargetingPageProps) {
	const [activeTab, setActiveTab] = useState<TargetingTabSlug>(
		props.initialTab ?? TARGETING_TABS[0].slug,
	);
	const [smartlinkId, setSmartlinkId] = useState<string>(props.initialSmartlinkId ?? "");

	useEffect(() => {
		if (props.initialTab && props.initialTab !== activeTab) {
			setActiveTab(props.initialTab);
		}
		// Only react to parent-provided tab changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.initialTab]);

	const trimmedId = smartlinkId.trim();

	const handleTabSelect = useCallback(
		(slug: TargetingTabSlug) => {
			setActiveTab(slug);
			props.onTabChange?.(slug);
		},
		[props],
	);

	return (
		<main
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16"
			data-testid="smartlink-targeting-page"
		>
			<Heading />
			<SmartlinkIdBar
				value={smartlinkId}
				onChange={setSmartlinkId}
				locked={Boolean(props.initialSmartlinkId)}
			/>
			<TabNav active={activeTab} onSelect={handleTabSelect} />
			<TabPanel tab={activeTab} smartlinkId={trimmedId} />
			<DocHint />
		</main>
	);
}

export default SmartlinkTargetingPage;

// ---------------------------------------------------------------------------
// Heading + shared controls — purely presentational.
// ---------------------------------------------------------------------------

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLinks
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Targeting hub
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Configure geo, OS, device, carrier, cookieless, mode, split, and gate
				rules for a SmartLink. Every tab scopes to the active SmartLink ID
				below.
			</p>
		</header>
	);
}

function SmartlinkIdBar(props: {
	value: string;
	onChange: (v: string) => void;
	locked: boolean;
}) {
	return (
		<section className={`${CARD} flex flex-col gap-2 p-5`} aria-labelledby="smartlink-id-label">
			<label id="smartlink-id-label" htmlFor="smartlink-targeting-id" className={LABEL}>
				Active SmartLink ID
			</label>
			<input
				id="smartlink-targeting-id"
				name="smartlinkId"
				type="text"
				autoComplete="off"
				placeholder="e.g. sl_01HQZPQ..."
				className={`${INPUT} max-w-md`}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				disabled={props.locked}
				data-testid="smartlink-id-input"
			/>
			<p className={HINT}>
				Changes apply at the tracker edge within seconds — no deploy step.
			</p>
		</section>
	);
}

function TabNav(props: {
	active: TargetingTabSlug;
	onSelect: (slug: TargetingTabSlug) => void;
}) {
	return (
		<nav
			aria-label="Targeting tabs"
			role="tablist"
			className={`${CARD} flex flex-wrap gap-1 overflow-hidden p-1`}
			data-testid="targeting-tab-nav"
		>
			{TARGETING_TABS.map((tab) => {
				const isActive = tab.slug === props.active;
				return (
					<button
						key={tab.slug}
						type="button"
						role="tab"
						aria-selected={isActive}
						aria-controls={`targeting-panel-${tab.slug}`}
						id={`targeting-tab-${tab.slug}`}
						data-testid={`targeting-tab-${tab.slug}`}
						onClick={() => props.onSelect(tab.slug)}
						className={`rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors ${
							isActive
								? "bg-zinc-900 text-white"
								: "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
						}`}
					>
						{tab.label}
					</button>
				);
			})}
		</nav>
	);
}

function TabPanel(props: { tab: TargetingTabSlug; smartlinkId: string }) {
	const tab = findTab(props.tab);
	return (
		<section
			role="tabpanel"
			id={`targeting-panel-${tab.slug}`}
			aria-labelledby={`targeting-tab-${tab.slug}`}
			data-testid={`targeting-panel-${tab.slug}`}
			className="flex flex-col gap-6"
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">{tab.label}</h2>
				<p className={HINT}>{tab.blurb}</p>
			</header>
			<TabForm slug={tab.slug} smartlinkId={props.smartlinkId} />
			<TabList slug={tab.slug} smartlinkId={props.smartlinkId} />
		</section>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How this hub works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>Tabs are independent — saving Geo does not affect OS or Device.</li>
				<li>Changes take effect on the next click; no deploy required.</li>
				<li>Deep-link any tab with ?smartlinkId=...&amp;tab=geo.</li>
			</ul>
		</aside>
	);
}

// ---------------------------------------------------------------------------
// List card — shared across tabs; renders a compact summary of saved rows.
// ---------------------------------------------------------------------------

function TabList(props: { slug: TargetingTabSlug; smartlinkId: string }) {
	const tab = findTab(props.slug);
	const smartlinkId = props.smartlinkId.length > 0 ? props.smartlinkId : null;
	const query = useQuery({
		queryKey: smartlinksTargetingQueryKeys.tabList(props.slug, smartlinkId),
		queryFn: () =>
			fetchTargetingList({ endpoint: tab.endpoint, smartlinkId }),
	});

	return (
		<section className={`${CARD} flex flex-col gap-3 p-5`}>
			<header className="flex items-center justify-between">
				<h3 className="text-sm font-semibold text-zinc-800">Saved rules</h3>
				<span className={HINT}>{query.isFetching ? "Refreshing." : "Auto-sync."}</span>
			</header>
			{query.isError && (
				<p className={ERROR_TEXT}>Unable to load saved rules. Retry in a moment.</p>
			)}
			{!query.isError && (query.data?.length ?? 0) === 0 && (
				<p className={HINT}>No rules yet for this tab.</p>
			)}
			{(query.data?.length ?? 0) > 0 && (
				<ul className="divide-y divide-zinc-100 rounded-[6px] border border-zinc-200">
					{query.data!.map((row: TargetingRow<unknown>) => (
						<li key={row.id} className="flex items-center justify-between gap-4 px-3 py-2">
							<div className="flex min-w-0 flex-col">
								<span className="truncate text-sm font-medium text-zinc-800">
									{row.smartlink_id}
								</span>
								<span className={HINT}>
									Updated {new Date(row.updated_at).toISOString().slice(0, 10)}
								</span>
							</div>
							<pre className="max-w-[60%] truncate rounded-[6px] bg-zinc-50 px-2 py-1 text-xs text-zinc-700">
								{JSON.stringify(row.config)}
							</pre>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Per-tab forms — each one is tiny. Keeping them in the same file so the
// strict-ownership lane stays at three files.
// ---------------------------------------------------------------------------

function TabForm(props: { slug: TargetingTabSlug; smartlinkId: string }) {
	switch (props.slug) {
		case "geo":
			return <GeoForm smartlinkId={props.smartlinkId} />;
		case "os":
			return <OsForm smartlinkId={props.smartlinkId} />;
		case "device":
			return <DeviceForm smartlinkId={props.smartlinkId} />;
		case "carrier":
			return <CarrierForm smartlinkId={props.smartlinkId} />;
		case "cookieless":
			return <CookielessForm smartlinkId={props.smartlinkId} />;
		case "mode":
			return <ModeForm smartlinkId={props.smartlinkId} />;
		case "split":
			return <SplitForm smartlinkId={props.smartlinkId} />;
		case "gate":
			return <GateForm smartlinkId={props.smartlinkId} />;
	}
}

// --- Shared save hook ------------------------------------------------------

function useSave<TConfig>(slug: TargetingTabSlug) {
	const tab = findTab(slug);
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (args: { smartlinkId: string; config: TConfig }) =>
			saveTargeting<TConfig>({
				endpoint: tab.endpoint,
				smartlinkId: args.smartlinkId,
				config: args.config,
			}),
		onSuccess: (row) => {
			toast.success(`${tab.label} rule saved.`);
			qc.invalidateQueries({ queryKey: [...smartlinksTargetingQueryKeys.all, slug] });
			qc.setQueryData(smartlinksTargetingQueryKeys.tabOne(slug, row.smartlink_id), row);
		},
		onError: (err: Error) => {
			toast.error(err.message || `Unable to save ${tab.label}.`);
		},
	});
}

// --- Geo -------------------------------------------------------------------

function GeoForm(props: { smartlinkId: string }) {
	const [mode, setMode] = useState<"allow" | "deny">("allow");
	const [countries, setCountries] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	const save = useSave<GeoConfig>("geo");

	useHydrate<GeoConfig>("geo", props.smartlinkId, (row) => {
		const cfg = row?.config ?? (defaultConfigFor("geo") as GeoConfig);
		setMode(cfg.mode);
		setCountries(cfg.countries.join(", "));
	});

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const idCheck = parseSmartlinkId(props.smartlinkId);
		if (!idCheck.success) return setError(idCheck.errors.smartlinkId ?? "Invalid ID");
		const codes = parseCsvCodes(countries, 250);
		if (!codes.success) return setError(codes.errors.codes ?? "Invalid countries");
		setError(null);
		save.mutate({ smartlinkId: idCheck.data, config: { mode, countries: codes.data } });
	}

	return (
		<form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<ModeToggle value={mode} onChange={setMode} labels={{ allow: "Allow", deny: "Deny" }} />
			<div className="flex flex-col gap-1.5">
				<label htmlFor="geo-countries" className={LABEL}>
					Country codes (ISO)
				</label>
				<textarea
					id="geo-countries"
					data-testid="geo-countries-input"
					className={`${INPUT} min-h-[72px]`}
					value={countries}
					onChange={(e) => setCountries(e.currentTarget.value)}
					placeholder="US, GB, DE, FR"
				/>
				<p className={HINT}>Comma- or space-separated ISO 3166-1 alpha-2 codes.</p>
			</div>
			<SubmitRow error={error} busy={save.isPending} />
		</form>
	);
}

// --- OS --------------------------------------------------------------------

function OsForm(props: { smartlinkId: string }) {
	const [mode, setMode] = useState<"allow" | "deny">("allow");
	const [osList, setOsList] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	const save = useSave<OsConfig>("os");

	useHydrate<OsConfig>("os", props.smartlinkId, (row) => {
		const cfg = row?.config ?? (defaultConfigFor("os") as OsConfig);
		setMode(cfg.mode);
		setOsList(cfg.os_list.join(", "));
	});

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const idCheck = parseSmartlinkId(props.smartlinkId);
		if (!idCheck.success) return setError(idCheck.errors.smartlinkId ?? "Invalid ID");
		const codes = parseCsvCodes(osList, 16);
		if (!codes.success) return setError(codes.errors.codes ?? "Invalid OS list");
		setError(null);
		save.mutate({
			smartlinkId: idCheck.data,
			config: { mode, os_list: codes.data.map((c) => c.toLowerCase()) },
		});
	}

	return (
		<form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<ModeToggle value={mode} onChange={setMode} labels={{ allow: "Allow", deny: "Deny" }} />
			<div className="flex flex-col gap-1.5">
				<label htmlFor="os-list" className={LABEL}>
					OS codes
				</label>
				<input
					id="os-list"
					data-testid="os-list-input"
					className={INPUT}
					value={osList}
					onChange={(e) => setOsList(e.currentTarget.value)}
					placeholder="ios, android, windows, macos"
				/>
				<p className={HINT}>Any mix of ios, android, windows, macos, linux, other.</p>
			</div>
			<SubmitRow error={error} busy={save.isPending} />
		</form>
	);
}

// --- Device ----------------------------------------------------------------

const DEVICE_OPTIONS: readonly { code: DeviceClass; label: string }[] = [
	{ code: "phone", label: "Phone" },
	{ code: "tablet", label: "Tablet" },
	{ code: "desktop", label: "Desktop" },
	{ code: "tv", label: "TV" },
];

function DeviceForm(props: { smartlinkId: string }) {
	const [mode, setMode] = useState<"allow" | "deny">("allow");
	const [selected, setSelected] = useState<DeviceClass[]>([]);
	const [error, setError] = useState<string | null>(null);
	const save = useSave<DeviceConfig>("device");

	useHydrate<DeviceConfig>("device", props.smartlinkId, (row) => {
		const cfg = row?.config ?? (defaultConfigFor("device") as DeviceConfig);
		setMode(cfg.mode);
		setSelected([...cfg.devices]);
	});

	function toggle(code: DeviceClass) {
		setSelected((prev) =>
			prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
		);
	}

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const idCheck = parseSmartlinkId(props.smartlinkId);
		if (!idCheck.success) return setError(idCheck.errors.smartlinkId ?? "Invalid ID");
		const devices = parseDeviceList(selected);
		if (!devices.success) return setError(devices.errors.devices ?? "Pick devices");
		setError(null);
		save.mutate({ smartlinkId: idCheck.data, config: { mode, devices: devices.data } });
	}

	return (
		<form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<ModeToggle value={mode} onChange={setMode} labels={{ allow: "Allow", deny: "Deny" }} />
			<fieldset className="flex flex-col gap-2">
				<legend className={LABEL}>Device classes</legend>
				<div className="flex flex-wrap gap-2" role="group" aria-label="Device classes">
					{DEVICE_OPTIONS.map((opt) => {
						const active = selected.includes(opt.code);
						return (
							<button
								key={opt.code}
								type="button"
								aria-pressed={active}
								data-testid={`device-chip-${opt.code}`}
								onClick={() => toggle(opt.code)}
								className={`inline-flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm font-medium transition-colors ${
									active
										? "border-emerald-500 bg-emerald-50 text-emerald-700"
										: "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
								}`}
							>
								{opt.label}
							</button>
						);
					})}
				</div>
			</fieldset>
			<SubmitRow error={error} busy={save.isPending} />
		</form>
	);
}

// --- Carrier ---------------------------------------------------------------

function CarrierForm(props: { smartlinkId: string }) {
	const [mode, setMode] = useState<"allow" | "deny">("allow");
	const [carriers, setCarriers] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	const save = useSave<CarrierConfig>("carrier");

	useHydrate<CarrierConfig>("carrier", props.smartlinkId, (row) => {
		const cfg = row?.config ?? (defaultConfigFor("carrier") as CarrierConfig);
		setMode(cfg.mode);
		setCarriers(cfg.carriers.join(", "));
	});

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const idCheck = parseSmartlinkId(props.smartlinkId);
		if (!idCheck.success) return setError(idCheck.errors.smartlinkId ?? "Invalid ID");
		const codes = parseCsvCodes(carriers, 128);
		if (!codes.success) return setError(codes.errors.codes ?? "Invalid carriers");
		setError(null);
		save.mutate({ smartlinkId: idCheck.data, config: { mode, carriers: codes.data } });
	}

	return (
		<form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<ModeToggle value={mode} onChange={setMode} labels={{ allow: "Allow", deny: "Deny" }} />
			<div className="flex flex-col gap-1.5">
				<label htmlFor="carrier-codes" className={LABEL}>
					Carrier codes
				</label>
				<textarea
					id="carrier-codes"
					data-testid="carrier-codes-input"
					className={`${INPUT} min-h-[72px]`}
					value={carriers}
					onChange={(e) => setCarriers(e.currentTarget.value)}
					placeholder="MCC-MNC or ISO carrier codes"
				/>
				<p className={HINT}>Match against mobile carrier codes in the click payload.</p>
			</div>
			<SubmitRow error={error} busy={save.isPending} />
		</form>
	);
}

// --- Cookieless ------------------------------------------------------------

function CookielessForm(props: { smartlinkId: string }) {
	const [enabled, setEnabled] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const save = useSave<CookielessConfig>("cookieless");

	useHydrate<CookielessConfig>("cookieless", props.smartlinkId, (row) => {
		const cfg = row?.config ?? (defaultConfigFor("cookieless") as CookielessConfig);
		setEnabled(cfg.enabled);
	});

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const idCheck = parseSmartlinkId(props.smartlinkId);
		if (!idCheck.success) return setError(idCheck.errors.smartlinkId ?? "Invalid ID");
		setError(null);
		save.mutate({ smartlinkId: idCheck.data, config: { enabled } });
	}

	return (
		<form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<label className="inline-flex items-center gap-3">
				<input
					type="checkbox"
					data-testid="cookieless-toggle"
					checked={enabled}
					onChange={(e) => setEnabled(e.currentTarget.checked)}
					className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
				/>
				<span className={LABEL}>Cookieless attribution enabled</span>
			</label>
			<p className={HINT}>
				When enabled, attribution relies on deterministic identifiers (IP + UA
				fingerprint fallback). Disable to use cookies only.
			</p>
			<SubmitRow error={error} busy={save.isPending} />
		</form>
	);
}

// --- Mode ------------------------------------------------------------------

function ModeForm(props: { smartlinkId: string }) {
	const [mode, setMode] = useState<"manual" | "auto">("manual");
	const [error, setError] = useState<string | null>(null);
	const save = useSave<ModeConfig>("mode");

	useHydrate<ModeConfig>("mode", props.smartlinkId, (row) => {
		const cfg = row?.config ?? (defaultConfigFor("mode") as ModeConfig);
		setMode(cfg.mode);
	});

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const idCheck = parseSmartlinkId(props.smartlinkId);
		if (!idCheck.success) return setError(idCheck.errors.smartlinkId ?? "Invalid ID");
		setError(null);
		save.mutate({ smartlinkId: idCheck.data, config: { mode } });
	}

	return (
		<form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<ModeToggle
				value={mode}
				onChange={setMode}
				labels={{ allow: "Manual", deny: "Auto" }}
				aValue="manual"
				bValue="auto"
			/>
			<p className={HINT}>
				Manual rotation uses operator-defined weights. Auto lets the ranker
				pick the next offer per click.
			</p>
			<SubmitRow error={error} busy={save.isPending} />
		</form>
	);
}

// --- Split -----------------------------------------------------------------

function SplitForm(props: { smartlinkId: string }) {
	const [rows, setRows] = useState<{ offer_id: string; bps: string }[]>([
		{ offer_id: "", bps: "10000" },
	]);
	const [error, setError] = useState<string | null>(null);
	const save = useSave<SplitConfig>("split");

	useHydrate<SplitConfig>("split", props.smartlinkId, (row) => {
		const cfg = row?.config ?? (defaultConfigFor("split") as SplitConfig);
		if (cfg.rules.length > 0) {
			setRows(cfg.rules.map((r: SplitRule) => ({ offer_id: r.offer_id, bps: String(r.bps) })));
		}
	});

	function addRow() {
		setRows((prev) => [...prev, { offer_id: "", bps: "0" }]);
	}
	function removeRow(i: number) {
		setRows((prev) => prev.filter((_, idx) => idx !== i));
	}
	function update(i: number, patch: Partial<{ offer_id: string; bps: string }>) {
		setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
	}

	function submit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const idCheck = parseSmartlinkId(props.smartlinkId);
		if (!idCheck.success) return setError(idCheck.errors.smartlinkId ?? "Invalid ID");
		const parsed = parseSplitRules(rows);
		if (!parsed.success) {
			const firstKey = Object.keys(parsed.errors)[0];
			return setError(parsed.errors[firstKey] ?? "Invalid split rules");
		}
		setError(null);
		save.mutate({ smartlinkId: idCheck.data, config: { rules: parsed.data } });
	}

	return (
		<form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-5`} noValidate>
			<div className="flex flex-col gap-3">
				{rows.map((r, i) => (
					<div
						key={i}
						className="grid grid-cols-[1fr_120px_auto] gap-2"
						data-testid={`split-row-${i}`}
					>
						<input
							className={INPUT}
							placeholder="Offer ID"
							value={r.offer_id}
							onChange={(e) => update(i, { offer_id: e.currentTarget.value })}
						/>
						<input
							className={INPUT}
							inputMode="numeric"
							placeholder="bps"
							value={r.bps}
							onChange={(e) => update(i, { bps: e.currentTarget.value })}
						/>
						<button
							type="button"
							onClick={() => removeRow(i)}
							className={BTN_SECONDARY}
							disabled={rows.length <= 1}
						>
							Remove
						</button>
					</div>
				))}
			</div>
			<button type="button" onClick={addRow} className={`${BTN_SECONDARY} self-start`}>
				Add offer
			</button>
			<p className={HINT}>Bps are basis points out of 10,000. Total must equal 10,000.</p>
			<SubmitRow error={error} busy={save.isPending} />
		</form>
	);
}

// --- Gate ------------------------------------------------------------------

function GateForm(props: { smartlinkId: string }) {
	const tab = findTab("gate");
	const smartlinkId = props.smartlinkId.length > 0 ? props.smartlinkId : null;
	const query = useQuery({
		queryKey: smartlinksTargetingQueryKeys.tabOne("gate", smartlinkId ?? ""),
		queryFn: () =>
			fetchTargetingOne<GateConfig>({
				endpoint: tab.endpoint,
				smartlinkId: smartlinkId!,
			}),
		enabled: smartlinkId !== null,
	});

	const gate = query.data?.config ?? (defaultConfigFor("gate") as GateConfig);

	return (
		<div className={`${CARD} flex flex-col gap-3 p-5`}>
			<div className="flex flex-col gap-1">
				<span className={LABEL}>Plan</span>
				<span className="text-sm text-zinc-900">{gate.plan}</span>
			</div>
			<div className="flex flex-col gap-1">
				<span className={LABEL}>Entitled</span>
				<span
					className={`inline-flex w-fit items-center gap-2 rounded-[6px] border px-2 py-0.5 text-xs font-medium ${
						gate.entitled
							? "border-emerald-300 bg-emerald-50 text-emerald-700"
							: "border-zinc-200 bg-zinc-50 text-zinc-700"
					}`}
					data-testid="gate-status"
				>
					{gate.entitled ? "Allowed" : "Blocked"}
				</span>
			</div>
			{gate.reason && (
				<div className="flex flex-col gap-1">
					<span className={LABEL}>Reason</span>
					<span className="text-sm text-zinc-900">{gate.reason}</span>
				</div>
			)}
			<p className={HINT}>
				Read-only. Upgrade the workspace plan to unlock blocked SmartLinks.
			</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Shared helpers — ModeToggle, SubmitRow, useHydrate.
// ---------------------------------------------------------------------------

function ModeToggle<T extends string>(props: {
	value: T;
	onChange: (v: T) => void;
	labels: { allow: string; deny: string };
	aValue?: T;
	bValue?: T;
}) {
	const a = (props.aValue ?? ("allow" as unknown)) as T;
	const b = (props.bValue ?? ("deny" as unknown)) as T;
	return (
		<div
			role="radiogroup"
			aria-label="Mode"
			className="inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
		>
			<button
				type="button"
				role="radio"
				aria-checked={props.value === a}
				onClick={() => props.onChange(a)}
				className={`rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors ${
					props.value === a
						? "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]"
						: "text-zinc-600 hover:text-zinc-900"
				}`}
			>
				{props.labels.allow}
			</button>
			<button
				type="button"
				role="radio"
				aria-checked={props.value === b}
				onClick={() => props.onChange(b)}
				className={`rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors ${
					props.value === b
						? "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]"
						: "text-zinc-600 hover:text-zinc-900"
				}`}
			>
				{props.labels.deny}
			</button>
		</div>
	);
}

function SubmitRow(props: { error: string | null; busy: boolean }) {
	return (
		<div className="flex flex-col gap-2">
			{props.error && <p className={ERROR_TEXT}>{props.error}</p>}
			<div className="flex items-center justify-end gap-2">
				<button type="submit" className={BTN_PRIMARY} disabled={props.busy}>
					{props.busy ? "Saving." : "Save rule"}
				</button>
			</div>
		</div>
	);
}

/**
 * Minimal hydrate helper — fetches the saved row for the active SmartLink and
 * calls back with the result exactly once per (tab, smartlinkId) tuple. Keeps
 * every form's useEffect pattern identical.
 */
function useHydrate<TConfig>(
	slug: TargetingTabSlug,
	smartlinkId: string,
	apply: (row: TargetingRow<TConfig> | null) => void,
) {
	const tab = findTab(slug);
	const enabled = smartlinkId.length > 0;
	const query = useQuery({
		queryKey: smartlinksTargetingQueryKeys.tabOne(slug, smartlinkId),
		queryFn: () =>
			fetchTargetingOne<TConfig>({ endpoint: tab.endpoint, smartlinkId }),
		enabled,
	});

	const data = query.data ?? null;
	const memoKey = useMemo(() => `${slug}|${smartlinkId}|${query.isSuccess}`, [
		slug,
		smartlinkId,
		query.isSuccess,
	]);

	useEffect(() => {
		if (!enabled) return;
		if (query.isSuccess) apply(data);
		// apply intentionally excluded — identity-stable by convention
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [memoKey]);
}
